import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { PlanNote } from "../components/PlanLockedHint";
import ProductImageManager from "../components/ProductImageManager";
import Toast from "../components/Toast";
import Toggle from "../components/Toggle";
import TranslatableField from "../components/TranslatableField";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { hasFeature } from "../services/features";
import { rememberedListUrl } from "../services/listMemory";
import { fetchBrands, fetchProductCategories } from "../services/catalogService";
import { createProduct, fetchProduct, updateProduct } from "../services/productService";
import { labelFor } from "../services/enumLabels";

/** Must match Product::UNITS on the API. */
const UNITS = ["piece", "box", "kg", "gram", "liter", "ml", "pack"];

const EMPTY = {
  name: { en: "", ar: "" },
  description: { en: "", ar: "" },
  price: "",
  unit: "piece",
  lowStockThreshold: "",
  categoryId: "",
  brandId: "",
  isAvailableOnline: true,
  isActive: true,
};

/** An API product -> the shape the form works with. */
const toForm = (product) => ({
  name: {
    en: product.translations?.name?.en ?? "",
    ar: product.translations?.name?.ar ?? "",
  },
  description: {
    en: product.translations?.description?.en ?? "",
    ar: product.translations?.description?.ar ?? "",
  },
  price: product.price ?? "",
  unit: product.unit ?? "piece",
  lowStockThreshold: product.low_stock_threshold == null ? "" : String(product.low_stock_threshold),
  categoryId: product.product_category_id ?? "",
  brandId: product.brand_id ?? "",
  isAvailableOnline: Boolean(product.is_available_online),
  isActive: Boolean(product.is_active),
});

/**
 * Client-side rules mirroring the API's, keyed the same way its 422 is, so both
 * sources of error land on the same input.
 */
const validate = (values, t) => {
  const errors = {};

  if (!values.name.en.trim()) errors["name.en"] = [t("products.form.errors.nameEn")];
  if (!values.name.ar.trim()) errors["name.ar"] = [t("products.form.errors.nameAr")];
  if (values.price === "" || Number.isNaN(Number(values.price)) || Number(values.price) < 0) {
    errors.price = [t("products.form.errors.price")];
  }
  if (
    values.lowStockThreshold !== "" &&
    (!Number.isInteger(Number(values.lowStockThreshold)) || Number(values.lowStockThreshold) < 0)
  ) {
    errors.low_stock_threshold = [t("products.form.errors.threshold")];
  }

  return errors;
};

/**
 * A catalogue product, defined once for the business like a service. Nothing
 * here touches a branch: which branches sell it, at what price and with what
 * opening stock, is the catalogue list's Branches action.
 */
export default function ProductForm() {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();

  const editing = Boolean(productId);
  const listPath = "/products";

  const [values, setValues] = useState(EMPTY);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [categoryList, brandList] = await Promise.all([
          fetchProductCategories(),
          fetchBrands(),
        ]);
        if (cancelled) return;
        setCategories(categoryList);
        setBrands(brandList);

        if (editing) {
          const product = await fetchProduct(productId);
          if (cancelled) return;
          setValues(toForm(product));
          setImages(product.images ?? []);
        }
      } catch (err) {
        if (!cancelled) setBanner(apiErrorMessage(err, t("products.form.loadFailed")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [productId, editing, i18n.language, t]);

  const set = (field) => (e) => setValues((v) => ({ ...v, [field]: e.target.value }));
  const fieldError = (name) => errors[name]?.[0];

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const clientErrors = validate(values, t);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      if (editing) {
        const product = await updateProduct(productId, values);
        // Stay put so the gallery can go on being edited.
        setToast({ type: "success", message: t("products.updated", { name: product.name }) });
      } else {
        const product = await createProduct(values);
        // Images need a saved product to hang off, so the new one opens in edit mode.
        navigate(`${listPath}/${product.id}/edit`, { replace: true });
        setToast({ type: "success", message: t("products.created", { name: product.name }) });
      }
    } catch (err) {
      setErrors(apiFieldErrors(err));
      setBanner(
        apiErrorMessage(err, editing ? t("products.form.updateFailed") : t("products.form.createFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">
            {editing ? t("products.form.editTitle") : t("products.form.createTitle")}
          </h1>
          <p className="text-sm text-ink-2">{t("products.form.catalogueSubtitle")}</p>
        </div>
        <button type="button" onClick={() => navigate(rememberedListUrl(listPath))} className="btn-secondary">
          {t("common.back")}
        </button>
      </div>

      {banner && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {banner}
        </div>
      )}

      <form onSubmit={submit} className="card p-5" noValidate>
        <fieldset disabled={saving} className="grid gap-4">
          <TranslatableField
            name="name"
            label={t("products.form.name")}
            value={values.name}
            onChange={(name) => setValues((v) => ({ ...v, name }))}
            errors={errors}
            required
          />

          <TranslatableField
            name="description"
            label={t("products.form.description")}
            value={values.description}
            onChange={(description) => setValues((v) => ({ ...v, description }))}
            errors={errors}
            textarea
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="categoryId">
                {t("products.form.category")}
              </label>
              <select
                id="categoryId"
                className="field-input"
                value={values.categoryId}
                onChange={set("categoryId")}
              >
                <option value="">{t("products.form.none")}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.parent ? `${category.parent.name} › ${category.name}` : category.name}
                  </option>
                ))}
              </select>
              {fieldError("product_category_id") && (
                <p className="field-error">{fieldError("product_category_id")}</p>
              )}
            </div>

            <div>
              <label className="field-label" htmlFor="brandId">
                {t("products.form.brand")}
              </label>
              <select
                id="brandId"
                className="field-input"
                value={values.brandId}
                onChange={set("brandId")}
              >
                <option value="">{t("products.form.none")}</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              {fieldError("brand_id") && <p className="field-error">{fieldError("brand_id")}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="price">
                {t("products.form.basePrice")}
                <span className="text-danger-text"> *</span>
              </label>
              <input
                id="price"
                type="number"
                step="0.01"
                min="0"
                dir="ltr"
                className="field-input"
                placeholder="0.00"
                value={values.price}
                onChange={set("price")}
              />
              <p className="mt-1 text-xs text-ink-2">{t("products.form.basePriceHint")}</p>
              {fieldError("price") && <p className="field-error">{fieldError("price")}</p>}
            </div>

            <div>
              <label className="field-label" htmlFor="unit">
                {t("products.form.unit")} <span className="text-danger-text">*</span>
              </label>
              <select id="unit" className="field-input" value={values.unit} onChange={set("unit")}>
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {labelFor("unit", unit)}
                  </option>
                ))}
              </select>
              {fieldError("unit") && <p className="field-error">{fieldError("unit")}</p>}
            </div>
          </div>

          {/* Inventory. Stock itself is per branch: opening stock is set when a
              branch is assigned, and moves through adjustments after that.
              Without the plan's inventory there is no threshold to set. */}
          {!hasFeature(getCurrentUser(), "inventory") ? (
            <PlanNote>{t("plan.inventoryOff")}</PlanNote>
          ) : (
            <div className="grid gap-4 rounded-lg border border-line p-3 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="lowStockThreshold">
                  {t("products.form.threshold")}
                </label>
                <input
                  id="lowStockThreshold"
                  type="number"
                  min="0"
                  step="1"
                  dir="ltr"
                  className="field-input"
                  placeholder={t("products.form.thresholdPlaceholder")}
                  value={values.lowStockThreshold}
                  onChange={set("lowStockThreshold")}
                />
                <p className="mt-1 text-xs text-ink-2">{t("products.form.thresholdHint")}</p>
                {fieldError("low_stock_threshold") && (
                  <p className="field-error">{fieldError("low_stock_threshold")}</p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3 rounded-lg border border-line p-3">
            <Toggle
              id="product-online"
              checked={values.isAvailableOnline}
              onChange={(checked) => setValues((v) => ({ ...v, isAvailableOnline: checked }))}
              label={t("products.form.availableOnline")}
              hint={t("products.form.availableOnlineHint")}
              error={fieldError("is_available_online")}
            />
            <Toggle
              id="product-active"
              checked={values.isActive}
              onChange={(checked) => setValues((v) => ({ ...v, isActive: checked }))}
              label={t("products.form.active")}
              hint={t("products.form.activeHint")}
              error={fieldError("is_active")}
            />
          </div>
        </fieldset>

        <div className="mt-6 flex gap-2 border-t border-line pt-5">
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? t("products.form.saving") : t("common.save")}
          </button>
          <button type="button" onClick={() => navigate(rememberedListUrl(listPath))} className="btn-secondary">
            {t("common.cancel")}
          </button>
        </div>
      </form>

      {/* Images need a saved product to belong to, so this appears after creation. */}
      <div className="card mt-4 p-5">
        {editing ? (
          <ProductImageManager
            productId={productId}
            images={images}
            onChange={setImages}
          />
        ) : (
          <p className="text-sm text-ink-2">{t("images.saveFirst")}</p>
        )}
      </div>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
